import { showToast } from '../../collections/CollectionUI.js';
import { ErrorHandler } from '../utils/error-handler.js';

/**
 * Open the edit modal for a collection.
 * @param {number} id - Collection identifier.
 * @returns {void}
 */
export function editCollection(id) {
    if (!id) {
        showToast('error', 'Collection ID không hợp lệ');
        return;
    }
    
    /* Show edit modal with collection data */
    this.showEditModal(id);
}

/**
 * Permanently delete a collection after confirmation.
 * @param {number} id - Collection identifier.
 * @returns {Promise<void>}
 */
export async function deleteCollection(id) {
    if (!confirm('Bạn có chắc chắn muốn xóa collection này? Thao tác này không thể hoàn tác.')) {
        return;
    }

    try {
        const response = await this.api.deleteCollection(id);

        if (response && response.success) {
            showToast('success', 'Xóa collection thành công!');
            this.loadCollections();
            if (typeof this.clearBulkSelection === 'function') {
                this.clearBulkSelection();
            }
        } else {
            throw new Error('Invalid response format');
        }
    } catch (error) {
        const message = ErrorHandler.extractMessage(error);
        ErrorHandler.handle(error, message);
    }
}

/**
 * Duplicate a collection and refresh the dashboard.
 * @param {number} id - Collection identifier.
 * @returns {Promise<void>}
 */
export async function duplicateCollection(id) {
    try {
        const response = await this.api.duplicateCollection(id);

        if (response && response.id) {
            showToast('success', 'Nhân bản collection thành công!');
            this.loadCollections();
        } else {
            throw new Error('Invalid response format');
        }
    } catch (error) {
        const message = ErrorHandler.extractMessage(error);
        ErrorHandler.handle(error, message);
    }
}

/**
 * Toggle collection visibility between public/private.
 * @param {number} id - Collection identifier.
 * @param {boolean} isPublic - Desired visibility state.
 * @returns {Promise<void>}
 */
export async function toggleVisibility(id, isPublic) {
    try {
        const response = await this.api.toggleVisibility(id, isPublic);

        if (response && response.id) {
            const status = isPublic ? 'công khai' : 'riêng tư';
            showToast('success', `Đã đặt collection thành ${status}`);
            this.loadCollections();
        } else {
            throw new Error('Invalid response format');
        }
    } catch (error) {
        const message = ErrorHandler.extractMessage(error);
        ErrorHandler.handle(error, message);
    }
}

/**
 * Copy a shareable URL for the collection to the clipboard.
 * @param {number} id - Collection identifier.
 * @returns {void}
 */
export function shareCollection(id) {
    const collection = this.collections.find((collectionItem) => collectionItem.id == id);
    if (!collection) return;

    let shareUrl;
    if (collection.is_public) {
        shareUrl = `${window.location.origin}/fdi?collection=${id}`;
    } else {
        shareUrl = `${window.location.origin}${window.location.pathname}?view=collections#collection-${id}`;
    }

    if (navigator.clipboard) {
        navigator.clipboard.writeText(shareUrl).then(() => {
            showToast('success', 'Đã sao chép liên kết vào clipboard!');
        }).catch(() => {
            this.fallbackCopyText(shareUrl);
        });
    } else {
        this.fallbackCopyText(shareUrl);
    }
}

/**
 * Fallback clipboard copy implementation.
 * @param {string} text - Text to copy.
 * @returns {void}
 */
export function fallbackCopyText(text) {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();

    try {
        document.execCommand('copy');
        showToast('Đã sao chép liên kết vào clipboard!', 'success');
    } catch (error) {
        showToast('error', 'Không thể sao chép liên kết. Vui lòng sao chép thủ công: ' + text);
    }

    document.body.removeChild(textArea);
}

/**
 * Open the public URL for a collection in a new tab.
 * @param {number} id - Collection identifier.
 * @returns {void}
 */
export function viewPublicCollection(id) {
    const url = `${window.location.origin}/fdi?collection=${id}`;
    window.open(url, '_blank');
}
